import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import DesignCanvas from './DesignCanvas';
import DesignSection from './DesignSection';
import DesignArtboard from './DesignArtboard';
import DesignRenderer from './DesignRenderer';
import DesignChat from './DesignChat';
import EditModeToolbar from './EditModeToolbar';
import PropertyPanel from './PropertyPanel';
import CommentPin from './CommentPin';
import CommentDialog from './CommentDialog';
import CommentThread from './CommentThread';
import DrawMode from './DrawMode';
import DrawToolbar from './DrawToolbar';
import PresentMode from './PresentMode';
import ProjectSettings from './ProjectSettings';
import FramePicker from './FramePicker';
import TweaksPanel from './TweaksPanel';
import DeviceFrame from './DeviceFrame';
import AndroidDevice from './AndroidDevice';
import WebBrowserFrame from './WebBrowserFrame';
import { createNapkin, addStroke, removeStroke, clearAll, serialize, deserialize } from './NapkinFormat';
import designStudioService from '../../services/designStudioService';
import designCommentService from '../../services/designCommentService';

// ── Artboard dimensions ──────────────────────────────────────────────────────
const ARTBOARD_W = 1200;
const ARTBOARD_H = 800;

// ── Helpers ───────────────────────────────────────────────────────────────────

const extractExplanation = (raw) => {
  try {
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}') + 1;
    if (start === -1 || end === 0) return raw.substring(0, 200);
    const data = JSON.parse(raw.substring(start, end));
    return data.explanation || '';
  } catch {
    return '';
  }
};

// ── File Tabs ──────────────────────────────────────────────────────────────────

const FileTabs = ({ files, activeFile, onSelect }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 0.5,
      px: 1.5,
      py: 0.75,
      overflowX: 'auto',
      background: '#fafafa',
      borderBottom: '1px solid #eee',
      '&::-webkit-scrollbar': { height: 3 },
    }}
  >
    {files.map((f) => (
      <Chip
        key={f.id}
        label={f.name}
        size="small"
        onClick={() => onSelect(f)}
        variant={activeFile?.id === f.id ? 'filled' : 'outlined'}
        sx={{
          fontSize: 11,
          height: 24,
          cursor: 'pointer',
          fontFamily: 'monospace',
          background: activeFile?.id === f.id ? '#1a1a1a' : 'transparent',
          color: activeFile?.id === f.id ? '#fff' : '#666',
          borderColor: '#ddd',
          '&:hover': { background: activeFile?.id === f.id ? '#333' : '#f0f0f0' },
        }}
      />
    ))}
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────────

const SplitViewDesignStudio = ({ projectId }) => {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('chat'); // 'chat' | 'edit' | 'comment' | 'files'
  const [activeFile, setActiveFile] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const streamRef = useRef(null);

  // Rendered files for the canvas
  const [renderedFiles, setRenderedFiles] = useState([]);

  // Edit mode — selected element
  const [selectedElement, setSelectedElement] = useState(null);
  const rendererRef = useRef(null);

  // Debounce timer for persisting style edits
  const editDebounceRef = useRef(null);
  const pendingEditsRef = useRef([]);

  // Comment mode
  const [comments, setComments] = useState([]);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  // New-comment dialog: { x, y } canvas fractions, null when no dialog open
  const [newCommentPos, setNewCommentPos] = useState(null);

  // Draw mode
  const [sketchFile, setSketchFile] = useState(null);
  const [napkin, setNapkin] = useState(createNapkin());
  const [drawColor, setDrawColor] = useState('#1a1a1a');
  const [drawSize, setDrawSize] = useState(2);
  const [erasing, setErasing] = useState(false);
  const sketchDebounceRef = useRef(null);

  // Device frame
  const [frameOption, setFrameOption] = useState({ key: 'none', label: 'No frame', dims: null });

  // Project settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);

  const canvasRef = useRef(null);

  // ── Load project ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await designStudioService.getProject(projectId);
        if (data) {
          setProject(data.project);
          setFiles(data.files || []);
          const convMessages = (data.project.conversations || []).map((m) => ({
            role: m.role,
            content: m.role === 'assistant' ? extractExplanation(m.content) : m.content,
          })).filter((m) => m.content);
          setMessages(convMessages);

          const renderables = (data.files || [])
            .filter((f) => f.content && f.content.trim().length > 10 && f.fileType !== 'sketch')
            .map((f) => ({ name: f.name, content: f.content, id: f.id }));
          setRenderedFiles(renderables);
        }
      } catch (err) {
        console.error('Failed to load design project:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  // ── Load sketch ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const loadSketch = async () => {
      try {
        const data = await designStudioService.getSketch(projectId);
        if (data) {
          setSketchFile(data);
          setNapkin(deserialize(data.content));
        }
      } catch (err) {
        console.error('Failed to load sketch:', err);
      }
    };
    loadSketch();
  }, [projectId]);

  // ── Load comments ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const loadComments = async () => {
      try {
        const data = await designCommentService.listComments(projectId);
        setComments(data || []);
      } catch (err) {
        console.error('Failed to load comments:', err);
      }
    };
    loadComments();
  }, [projectId]);

  // ── Send prompt ───────────────────────────────────────────────────────────────

  const handleSend = useCallback(async (prompt) => {
    setMessages((prev) => [...prev, { role: 'user', content: prompt }]);
    setIsStreaming(true);
    setStreamingText('');
    let accumulated = '';
    const convHistory = messages.map((m) => ({ role: m.role, content: m.content }));
    const stream = designStudioService.generateDesign(projectId, prompt, convHistory);
    streamRef.current = stream;

    try {
      for await (const event of stream.events()) {
        if (event.error) {
          setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${event.content}` }]);
          break;
        }
        if (event.done) {
          const savedFiles = event.files || [];
          const explanation = extractExplanation(accumulated);
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: explanation || 'Design generated.',
            filesGenerated: savedFiles.length,
          }]);
          if (savedFiles.length > 0) {
            setFiles((prev) => {
              const byId = new Map(prev.map((f) => [f.id, f]));
              savedFiles.forEach((f) => byId.set(f.id, f));
              return Array.from(byId.values());
            });
            setRenderedFiles(
              savedFiles
                .filter((f) => f.content && f.content.trim().length > 10 && f.fileType !== 'sketch')
                .map((f) => ({ name: f.name, content: f.content, id: f.id }))
            );
          }
          break;
        }
        if (event.content) {
          accumulated += event.content;
          setStreamingText(accumulated.substring(0, 500));
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) => [...prev, { role: 'assistant', content: `Stream error: ${err.message}` }]);
      }
    } finally {
      setIsStreaming(false);
      setStreamingText('');
      streamRef.current = null;
    }
  }, [messages, projectId]);

  useEffect(() => () => streamRef.current?.cancel(), []);

  // ── Edit mode — element selection ────────────────────────────────────────────

  const handleElementSelected = useCallback((info) => {
    setSelectedElement({ elementId: info.elementId, tagName: info.tagName, styles: info.styles || {} });
  }, []);

  const handleElementDeselected = useCallback(() => {
    setSelectedElement(null);
  }, []);

  const handleStyleChange = useCallback((property, value) => {
    if (!selectedElement) return;

    setSelectedElement((prev) => prev
      ? { ...prev, styles: { ...prev.styles, [property]: value } }
      : null);

    rendererRef.current?.previewStyle(selectedElement.elementId, { [property]: value });

    pendingEditsRef.current = [
      ...pendingEditsRef.current.filter((e) => e.property !== property),
      { elementId: selectedElement.elementId, property, value: String(value) },
    ];

    clearTimeout(editDebounceRef.current);
    editDebounceRef.current = setTimeout(async () => {
      const edits = pendingEditsRef.current;
      pendingEditsRef.current = [];
      if (!edits.length) return;

      const targetFile = renderedFiles.find((f) =>
        f.name.includes('entry') || f.name.includes('canvas')
      ) || renderedFiles[renderedFiles.length - 1];
      if (!targetFile?.id) return;

      try {
        const updated = await designStudioService.applyEdits(targetFile.id, edits);
        if (updated) {
          setRenderedFiles((prev) => prev.map((f) => f.id === updated.id ? { ...f, content: updated.content } : f));
          setFiles((prev) => prev.map((f) => f.id === updated.id ? { ...f, content: updated.content } : f));
        }
      } catch (err) {
        console.error('Style persist failed:', err);
      }
    }, 800);
  }, [selectedElement, renderedFiles]);

  // ── Comment mode ─────────────────────────────────────────────────────────────

  const handleCanvasClick = useCallback((x, y) => {
    if (mode !== 'comment') return;
    setNewCommentPos({ x, y });
    setSelectedCommentId(null);
  }, [mode]);

  const handleSaveComment = useCallback(async (text) => {
    if (!newCommentPos) return;
    try {
      const comment = await designCommentService.createComment(projectId, {
        x: newCommentPos.x,
        y: newCommentPos.y,
        text,
      });
      setComments((prev) => [...prev, comment]);
      setSelectedCommentId(comment.id);
      setNewCommentPos(null);
    } catch (err) {
      console.error('Create comment failed:', err);
    }
  }, [newCommentPos, projectId]);

  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await designCommentService.deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setSelectedCommentId(null);
    } catch (err) {
      console.error('Delete comment failed:', err);
    }
  }, []);

  const handleUpdateComment = useCallback(async (commentId, data) => {
    try {
      const updated = await designCommentService.updateComment(commentId, data);
      if (updated) {
        setComments((prev) => prev.map((c) => c.id === commentId ? updated : c));
      }
    } catch (err) {
      console.error('Update comment failed:', err);
    }
  }, []);

  const handleAddReply = useCallback(async (commentId, text) => {
    try {
      const updated = await designCommentService.addReply(commentId, text);
      if (updated) {
        setComments((prev) => prev.map((c) => c.id === commentId ? updated : c));
      }
    } catch (err) {
      console.error('Add reply failed:', err);
    }
  }, []);

  // Called when Claude finishes a design revision triggered from a comment
  const handleDesignUpdated = useCallback((savedFiles) => {
    if (!savedFiles.length) return;
    setFiles((prev) => {
      const byId = new Map(prev.map((f) => [f.id, f]));
      savedFiles.forEach((f) => byId.set(f.id, f));
      return Array.from(byId.values());
    });
    setRenderedFiles(
      savedFiles
        .filter((f) => f.content && f.content.trim().length > 10 && f.fileType !== 'sketch')
        .map((f) => ({ name: f.name, content: f.content, id: f.id }))
    );
  }, []);

  // ── Draw mode ─────────────────────────────────────────────────────────────────

  const saveSketch = useCallback((updatedNapkin) => {
    if (!sketchFile?.id) return;
    clearTimeout(sketchDebounceRef.current);
    sketchDebounceRef.current = setTimeout(async () => {
      try {
        await designStudioService.updateSketch(sketchFile.id, serialize(updatedNapkin));
      } catch (err) {
        console.error('Sketch save failed:', err);
      }
    }, 1000);
  }, [sketchFile]);

  const handleStrokeEnd = useCallback((points) => {
    const updated = addStroke(napkin, points, drawColor, drawSize);
    setNapkin(updated);
    saveSketch(updated);
  }, [napkin, drawColor, drawSize, saveSketch]);

  const handleEraseStroke = useCallback((strokeId) => {
    const updated = removeStroke(napkin, strokeId);
    setNapkin(updated);
    saveSketch(updated);
  }, [napkin, saveSketch]);

  const handleDrawUndo = useCallback(() => {
    const strokes = napkin.objects.filter((o) => o.type === 'stroke');
    if (!strokes.length) return;
    const lastId = strokes[strokes.length - 1].id;
    const updated = removeStroke(napkin, lastId);
    setNapkin(updated);
    saveSketch(updated);
  }, [napkin, saveSketch]);

  const handleDrawClear = useCallback(() => {
    const updated = clearAll(napkin);
    setNapkin(updated);
    saveSketch(updated);
  }, [napkin, saveSketch]);

  // ── Project settings ──────────────────────────────────────────────────────────

  const handleSaveSettings = useCallback(async (data) => {
    try {
      const updated = await designStudioService.updateProject(projectId, data);
      if (updated) setProject(updated);
    } catch (err) {
      console.error('Update project failed:', err);
    }
  }, [projectId]);

  const handleDuplicateProject = useCallback(async () => {
    try {
      const result = await designStudioService.duplicateProject(projectId);
      if (result?.project?.id) navigate(`/design-studio/${result.project.id}`);
    } catch (err) {
      console.error('Duplicate failed:', err);
    }
  }, [projectId, navigate]);

  const handleDuplicateAsTemplate = useCallback(async () => {
    try {
      await designStudioService.duplicateAsTemplate(projectId);
    } catch (err) {
      console.error('Duplicate as template failed:', err);
    }
  }, [projectId]);

  const handleDeleteProject = useCallback(async () => {
    try {
      await designStudioService.deleteProject(projectId);
      navigate('/design-studio');
    } catch (err) {
      console.error('Delete project failed:', err);
    }
  }, [projectId, navigate]);

  // ── Mode change ───────────────────────────────────────────────────────────────

  const handleModeChange = (newMode) => {
    if (newMode === 'present') {
      setMode('present');
      return;
    }
    setMode(newMode);
    if (newMode !== 'edit') {
      setSelectedElement(null);
      rendererRef.current?.deselect();
    }
    if (newMode !== 'comment') {
      setNewCommentPos(null);
    }
    if (newMode !== 'draw') {
      setErasing(false);
    }
    // tweaks mode shows left panel — no other state changes needed
  };

  const handleZoomIn = () => canvasRef.current?.zoomIn?.();
  const handleZoomOut = () => canvasRef.current?.zoomOut?.();
  const handleFit = () => canvasRef.current?.fit?.();

  // ── Computed ─────────────────────────────────────────────────────────────────

  const selectedComment = comments.find((c) => c.id === selectedCommentId) || null;
  const unresolvedCount = comments.filter((c) => !c.resolved).length;

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasRenderable = renderedFiles.length > 0;
  const screenFiles = renderedFiles.filter((f) => !f.name.includes('entry') && !f.name.includes('canvas'));
  const entryFiles = renderedFiles.filter((f) => f.name.includes('entry') || f.name.includes('canvas'));
  const filesToRender = screenFiles.length > 0 ? [...screenFiles, ...entryFiles] : renderedFiles;

  const isEditMode = mode === 'edit';
  const isCommentMode = mode === 'comment';
  const isFilesMode = mode === 'files';
  const isDrawMode = mode === 'draw';
  const isPresentMode = mode === 'present';
  const isTweaksMode = mode === 'tweaks';

  // Frame-aware artboard dimensions
  const artboardW = frameOption.dims ? frameOption.dims.width : ARTBOARD_W;
  const artboardH = frameOption.dims ? frameOption.dims.height : ARTBOARD_H;

  // Wrap DesignRenderer in the appropriate device frame
  const wrapWithFrame = (rendererNode) => {
    const fk = frameOption.key;
    if (fk === 'none' || !frameOption.dims) return rendererNode;
    if (fk.startsWith('ios')) {
      return (
        <DeviceFrame width={artboardW} height={artboardH}>
          {rendererNode}
        </DeviceFrame>
      );
    }
    if (fk.startsWith('android')) {
      return (
        <AndroidDevice width={artboardW} height={artboardH}>
          {rendererNode}
        </AndroidDevice>
      );
    }
    if (fk.startsWith('web')) {
      return (
        <WebBrowserFrame width={artboardW} height={artboardH}>
          {rendererNode}
        </WebBrowserFrame>
      );
    }
    return rendererNode;
  };

  // Build the draw overlay for the artboard
  const drawOverlay = isDrawMode ? (
    <>
      <DrawMode
        napkin={napkin}
        active={true}
        erasing={erasing}
        color={drawColor}
        size={drawSize}
        width={artboardW}
        height={artboardH}
        onStrokeEnd={handleStrokeEnd}
        onEraseStroke={handleEraseStroke}
      />
      <DrawToolbar
        color={drawColor}
        onColorChange={setDrawColor}
        size={drawSize}
        onSizeChange={setDrawSize}
        erasing={erasing}
        onErasingChange={setErasing}
        onUndo={handleDrawUndo}
        onClear={handleDrawClear}
      />
    </>
  ) : null;

  // Build the pins overlay for the artboard
  const pinsOverlay = isCommentMode ? (
    <>
      {comments.map((c, i) => (
        <CommentPin
          key={c.id}
          comment={c}
          index={i + 1}
          artboardW={artboardW}
          artboardH={artboardH}
          selected={c.id === selectedCommentId}
          onClick={(id) => {
            setSelectedCommentId(id);
            setNewCommentPos(null);
          }}
        />
      ))}

      {/* New comment dialog — positioned near the click point */}
      {newCommentPos && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(newCommentPos.x * artboardW, artboardW - 310),
            top: Math.min(newCommentPos.y * artboardH + 16, artboardH - 340),
            pointerEvents: 'auto',
            zIndex: 30,
          }}
        >
          <CommentDialog
            comment={null}
            onClose={() => setNewCommentPos(null)}
            onSave={handleSaveComment}
          />
        </div>
      )}

      {/* Existing comment dialog */}
      {selectedCommentId && selectedComment && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(selectedComment.x * artboardW + 16, artboardW - 310),
            top: Math.max(selectedComment.y * artboardH - 24, 4),
            pointerEvents: 'auto',
            zIndex: 30,
          }}
        >
          <CommentDialog
            comment={selectedComment}
            commentNumber={comments.indexOf(selectedComment) + 1}
            onClose={() => setSelectedCommentId(null)}
            onDelete={handleDeleteComment}
            onUpdate={handleUpdateComment}
            onReply={handleAddReply}
            onDesignUpdated={handleDesignUpdated}
          />
        </div>
      )}
    </>
  ) : null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toolbar */}
      <EditModeToolbar
        mode={mode}
        onModeChange={handleModeChange}
        projectName={project?.name}
        onBack={() => navigate('/design-studio')}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFit={handleFit}
        unresolvedCommentCount={unresolvedCount}
        projectId={projectId}
        onSettingsOpen={() => setSettingsOpen(true)}
        framePicker={
          <FramePicker
            frameType={frameOption.key}
            onFrameChange={setFrameOption}
          />
        }
      />

      {/* Split view body */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left panel — chat / files / comment thread / tweaks */}
        {!isEditMode && !isDrawMode && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: '1px solid #eee',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {isTweaksMode ? (
              <TweaksPanel
                projectId={projectId}
                onApplyComplete={() => {
                  designStudioService.getProject(projectId).then((data) => {
                    if (data?.files) {
                      const renderables = data.files
                        .filter((f) => f.content && f.content.trim().length > 10 && f.fileType !== 'sketch')
                        .map((f) => ({ name: f.name, content: f.content, id: f.id }));
                      setRenderedFiles(renderables);
                    }
                  }).catch(() => {});
                }}
              />
            ) : isCommentMode ? (
              <CommentThread
                comments={comments}
                selectedId={selectedCommentId}
                onSelect={(id) => {
                  setSelectedCommentId(id);
                  setNewCommentPos(null);
                }}
              />
            ) : isFilesMode ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <FileTabs files={files} activeFile={activeFile} onSelect={setActiveFile} />
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    color: '#333',
                    background: '#fafafa',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {activeFile?.content || files[0]?.content || '// No files yet'}
                </Box>
              </Box>
            ) : (
              <DesignChat
                messages={messages}
                isStreaming={isStreaming}
                streamingText={streamingText}
                onSend={handleSend}
                disabled={loading}
              />
            )}
          </Box>
        )}

        {/* Center — canvas */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {hasRenderable ? (
            <DesignCanvas ref={canvasRef} initialScale={0.6}>
              <DesignSection title="Generated Design">
                <DesignArtboard
                  label={frameOption.dims ? frameOption.label : 'Preview'}
                  width={artboardW}
                  height={artboardH}
                  overlay={pinsOverlay || drawOverlay}
                >
                  {wrapWithFrame(
                    <DesignRenderer
                      ref={rendererRef}
                      files={filesToRender}
                      width={artboardW}
                      height={artboardH}
                      editMode={isEditMode}
                      commentMode={isCommentMode}
                      onElementSelected={handleElementSelected}
                      onElementDeselected={handleElementDeselected}
                      onCanvasClick={handleCanvasClick}
                    />
                  )}
                </DesignArtboard>
              </DesignSection>
            </DesignCanvas>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f0eee9',
                gap: 2,
                opacity: 0.6,
                userSelect: 'none',
              }}
            >
              <Typography variant="h6" sx={{ color: '#888', fontWeight: 500 }}>Canvas</Typography>
              <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center', maxWidth: 280 }}>
                {isStreaming ? 'Generating your design…' : 'Send a prompt on the left to generate a design'}
              </Typography>
              {isStreaming && <CircularProgress size={24} sx={{ color: '#888' }} />}
            </Box>
          )}
        </Box>

        {/* Right panel — property panel (edit mode only) */}
        {isEditMode && (
          <PropertyPanel
            selectedElement={selectedElement}
            onClose={() => handleModeChange('chat')}
            onStyleChange={handleStyleChange}
          />
        )}
      </Box>

      {/* Present mode portal */}
      {isPresentMode && (
        <PresentMode
          slides={[filesToRender]}
          initialIndex={0}
          onClose={() => handleModeChange('chat')}
        />
      )}

      {/* Project settings dialog */}
      <ProjectSettings
        open={settingsOpen}
        project={project}
        onClose={() => setSettingsOpen(false)}
        onSave={handleSaveSettings}
        onDuplicate={handleDuplicateProject}
        onDuplicateTemplate={handleDuplicateAsTemplate}
        onDelete={handleDeleteProject}
      />
    </Box>
  );
};

export default SplitViewDesignStudio;
